import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Available PO split strategies.
 * Add new strategies here when implementing them in the backend registry.
 */
export const PO_SPLIT_STRATEGIES = ["ship-from-location"] as const;
export type POSplitStrategyName = (typeof PO_SPLIT_STRATEGIES)[number];

/**
 * Split Staged PO Step
 *
 * Optionally splits a staged purchase order into multiple orders based on a
 * configurable strategy. The strategy determines how line items are categorized
 * and which metadata hints to apply to each resulting order.
 *
 * Use cases:
 * - Split by ship-from location (e.g., Zhuhai vs US warehouse)
 * - Split by product type (e.g., equipment vs parts)
 * - Any other line-item-based categorization
 *
 * The step:
 * - Loads the staged PO and its matched product variants
 * - Categorizes each line item using the configured strategy
 * - If all items are in one category, returns the original PO unchanged
 * - If items span multiple categories, creates additional staged POs
 * - Applies metadata hints (order type, warehouse) from the strategy
 *
 * Note: This is a backend-only step that requires database access.
 * The handler is registered in backend/src/workflows/steps/order-management/
 */
export const splitStagedPOStep = defineStepConfig({
  type: "split_staged_po",
  name: "Split Staged PO",
  description:
    "Optionally splits a staged PO into multiple orders based on a configurable strategy",
  category: "Data Access",
  libraryModule: "OrderManagement",
  deprecated: {
    reason:
      "Superseded by the workflow-native draft-sales-order flow. Use 'Split Draft Sales Order', which splits relational draft sales orders via declarative split rules (condition groups over line/variant fields) instead of a hardcoded strategy.",
    replacedBy: "split_draft_sales_order",
  },
  inputSchema: z.object({
    stagedPurchaseOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of the staged purchase orders to potentially split"),
  }),

  outputSchema: z.object({
    stagedPurchaseOrderIds: z
      .array(z.string().uuid())
      .describe(
        "UUIDs of all staged purchase orders (originals + any created by splits)",
      ),
    splitCount: z
      .number()
      .int()
      .describe("Number of POs that were actually split"),
  }),

  configSchema: z.object({
    strategyName: z
      .enum(PO_SPLIT_STRATEGIES)
      .describe("Name of the split strategy to use"),
    disabled: z
      .boolean()
      .default(false)
      .describe("If true, skip splitting and pass through original PO"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: {
      maximumAttempts: 2,
    },
    nonRetryableErrorTypes: ["ValidationError", "SchemaValidationError"],
  },
});
